import { NgModule, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageModule } from 'primeng/message';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CardModule } from 'primeng/card';
import { ListboxModule } from 'primeng/listbox';
import { DragDropModule } from 'primeng/dragdrop';
import { CommonModule } from '@angular/common';
import { MenuModule } from 'primeng/menu';
import { SidebarModule } from 'primeng/sidebar';
import { SlideMenuModule } from 'primeng/slidemenu';
import { BadgeModule } from 'primeng/badge';
import { MenubarModule } from 'primeng/menubar';
import { PanelMenuModule } from 'primeng/panelmenu';
import { InputSwitchModule } from 'primeng/inputswitch';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TreeTableModule } from 'primeng/treetable';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { SidebarComponent } from './navigation/sidebar/sidebar.component';
import { AvatarModule } from 'primeng/avatar';
import { DragAndDropComponent } from './dispatch/drag-and-drop/drag-and-drop.component';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ToolbarModule } from 'primeng/toolbar';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DividerModule } from 'primeng/divider';
import { SpeedDialModule } from 'primeng/speeddial';
import { ToolbarComponent } from './dispatch/toolbar/toolbar.component';
import { DialogModule } from 'primeng/dialog';
import { LoadLogisticsComponent } from './dispatch/load-logistics/load-logistics.component';
import { TooltipModule } from 'primeng/tooltip';
import { AccordionModule } from 'primeng/accordion';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { HTTP_INTERCEPTORS, HttpClientModule, provideHttpClient, withInterceptors } from '@angular/common/http';
import { TrailerTrackerComponent } from './dispatch/trailer-tracker/trailer-tracker.component';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { AuthCallbackComponent } from './routing/auth-callback/auth-callback.component';
import { provideRouter, RouterModule, Routes, Router } from '@angular/router';
import { LoginComponent } from './navigation/login/login.component';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { OrdersComponent } from './orders/orders.component';
import { DispatchDashboardComponent } from './dispatch-dashboard/dispatch-dashboard.component';
import { HeaderComponent } from './header/header.component';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { FloatLabelModule } from 'primeng/floatlabel';
import { DriversComponent } from './drivers/drivers.component';
import { EquipmentComponent } from './equipment/equipment.component';
import { CustomersComponent } from './customers/customers.component';
import { PaginatorModule } from 'primeng/paginator';
import { InputTextModule } from 'primeng/inputtext';
import { SpeedDialItemsComponent } from './v2-dispatch-components/speed-dial-items/speed-dial-items.component';
import { TabViewModule } from 'primeng/tabview';
import { DriversScheduleComponent } from './drivers-schedule/drivers-schedule.component';
import { CarouselModule } from 'primeng/carousel'
import { ChartModule } from 'primeng/chart';
import { DriversChartsComponent } from './drivers-charts/drivers-charts.component';
import { TabMenuModule } from 'primeng/tabmenu';
import { DriversNotesComponent } from './drivers-notes/drivers-notes.component';
import { ChipModule } from 'primeng/chip';
import { InplaceModule } from 'primeng/inplace';
import { AuthGuard } from './routing/auth.guard';
import { CustomerLoadDetailsComponent } from './customers/customer-load-details/customer-load-details.component';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ManageDriversComponent } from './manage-drivers/manage-drivers.component';
import { FieldsetModule } from 'primeng/fieldset';
import { SelectButtonModule } from 'primeng/selectbutton';
import { MultiSelectModule } from 'primeng/multiselect';
import { SendDispatchComponent } from './dispatch/send-dispatch/send-dispatch.component';
import { IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { MSAL_INSTANCE, MsalService } from '@azure/msal-angular';
import { authInterceptor } from './interceptors/auth.interceptor';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { AppInitService } from './services/app-init.service';
import { environment } from '../environments/environment';
import { CustomerFormComponent } from './customers/customer-extra-components/customer-form/customer-form.component';
import { ContactManagementComponent } from './customers/customer-extra-components/contact-management/contact-management.component';
import { AddressManagementComponent } from './customers/customer-extra-components/address-management/address-management.component';
import { AssignEquipmentComponent } from './equipment/assign-equipment/assign-equipment.component';
import { StatusUpdateDialogComponent } from './dispatch-dash-dialogs/status-update-dialog/status-update-dialog.component';
import { TimeEditDialogComponent } from './dispatch-dash-dialogs/time-edit-dialog/time-edit-dialog.component';
import { MiscTaskDialogComponent } from './dispatch-dash-dialogs/misc-task-dialog/misc-task-dialog.component';
import { EquipmentEditDialogComponent } from './dispatch-dash-dialogs/equipment-edit-dialog/equipment-edit-dialog.component';
import { SaltTypeDialogComponent } from './dispatch-dash-dialogs/salt-type-dialog/salt-type-dialog.component';
import { AssignToNewOrderDialogComponent } from './dispatch-dash-dialogs/assign-to-new-order-dialog/assign-to-new-order-dialog.component';
import { WashoutDialogComponent } from './dispatch-dash-dialogs/washout-dialog/washout-dialog.component';
import { EditOrderDialogComponent } from './order-management/edit-order-dialog/edit-order-dialog.component';
import { NewOrderDialogComponent } from './order-management/new-order-dialog/new-order-dialog.component';
import { OrderCalendarComponent } from './order-calendar/order-calendar/order-calendar.component';
import { MonthCalendarComponent } from './order-calendar/month-calendar/month-calendar.component';
import { WeekCalendarComponent } from './order-calendar/week-calendar/week-calendar.component';
import { TenderDialogComponent } from './order-management/tender-dialog/tender-dialog.component';
import { SkeletonModule } from 'primeng/skeleton';
import { ProgressBarModule } from 'primeng/progressbar';
import { InputNumberModule } from 'primeng/inputnumber';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import * as Sentry from "@sentry/angular";
import { ButtonClickLogDirective } from './directives/button-click-log.directive';

// Function to initialize the app
export function initializeApp(appInitService: AppInitService) {
  return () => appInitService.initializeApp();
}

// MSAL instance factory with verbose logging
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.backendClientId, // Replace with your Azure AD app registration client ID
      authority: `https://login.microsoftonline.com/${environment.azureTenantId}`, // Replace with your tenant ID
      // redirectUri: 'http://localhost:4200', // Your app's redirect URI
      redirectUri: 'https://dispatch-app-dev.fly.dev'
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: true
    },
    system: {
      loggerOptions: {
        loggerCallback: (level: any, message: any, containsPii: any) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case LogLevel.Error:
              console.error(message);
              break;
            case LogLevel.Info:
              console.info(message);
              break;
            case LogLevel.Verbose:
              console.debug(message);
              break;
            case LogLevel.Warning:
              console.warn(message);
              break;
          }
        },
        piiLoggingEnabled: false,
        logLevel: LogLevel.Verbose
      }
    }
  });
}


const routes: Routes = [
  // Your other routes...
  { path: 'auth-callback', component: AuthCallbackComponent },
];

@NgModule({
  declarations: [
    AppComponent,
    SidebarComponent,
    DragAndDropComponent,
    ToolbarComponent,
    LoadLogisticsComponent,
    TrailerTrackerComponent,
    AuthCallbackComponent,
    OrdersComponent,
    DispatchDashboardComponent,
    HeaderComponent,
    DriversComponent,
    EquipmentComponent,
    CustomersComponent,
    SpeedDialItemsComponent,
    DriversScheduleComponent,
    DriversChartsComponent,
    DriversNotesComponent,
    CustomerLoadDetailsComponent,
    ManageDriversComponent,
    SendDispatchComponent,
    LoginComponent,
    CustomerFormComponent,
    ContactManagementComponent,
    AddressManagementComponent,
    AssignEquipmentComponent,
    StatusUpdateDialogComponent,
    TimeEditDialogComponent,
    MiscTaskDialogComponent,
    EquipmentEditDialogComponent,
    SaltTypeDialogComponent,
    AssignToNewOrderDialogComponent,
    WashoutDialogComponent,
    EditOrderDialogComponent,
    NewOrderDialogComponent,
    OrderCalendarComponent,
    MonthCalendarComponent,
    WeekCalendarComponent,
    TenderDialogComponent,
    ButtonClickLogDirective
  ],
  imports: [
    BrowserModule, AppRoutingModule, ButtonModule, ToastModule, MessageModule, CardModule, ListboxModule, DragDropModule, CommonModule, MenuModule,
    SidebarModule, SlideMenuModule, BadgeModule, MenuModule, MenubarModule, PanelMenuModule, InputSwitchModule, FormsModule,
    TreeTableModule, TableModule, TagModule, ScrollPanelModule, AvatarModule, CheckboxModule, InputTextareaModule,
    InputGroupAddonModule, InputGroupModule, ToolbarModule, CalendarModule, DropdownModule, AutoCompleteModule, DividerModule,
    SpeedDialModule, DialogModule, TooltipModule, AccordionModule, ConfirmDialogModule,
    HttpClientModule, ConfirmPopupModule, RouterModule.forRoot(routes), ProgressSpinnerModule, InputIconModule, IconFieldModule, FloatLabelModule,
    PaginatorModule, InputTextModule, TabViewModule, FormsModule, ReactiveFormsModule, CarouselModule, BrowserAnimationsModule, ChartModule, TabMenuModule,
    ChipModule, InputTextModule, InplaceModule, RadioButtonModule, FieldsetModule, SelectButtonModule, MultiSelectModule, SkeletonModule, ProgressBarModule, InputNumberModule, OverlayPanelModule
    // MsalModule // Comment out MsalModule
  ],
  providers: [
    provideAnimations(),
    MessageService,
    ConfirmationService,
    AuthGuard,
    provideHttpClient(),
    provideRouter(routes),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    MsalService,
    provideHttpClient(withInterceptors([authInterceptor])),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    AppInitService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AppInitService],
      multi: true
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent, SidebarComponent]
})
export class AppModule {
  constructor() {
    
  }
}


